import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Wallet,
  DollarSign,
  Info,
  Check,
  Banknote,
  Send,
  MessageSquare,
  HelpCircle,
  Calendar,
} from "lucide-react";
import {
  useGetCustomerPaymentTerms,
  usePaymentInstructions,
} from "@/api/payments";
import { useGetCustomerCreditBalance } from "@/api/customer-credit";
import { useCustomerIdentity } from "@/api/customers";
import { formatCurrency } from "@/lib/format";

export type PaymentMethodType =
  | "SAVED_CARD"
  | "CREDIT"
  | "INVOICE"
  | "CASH"
  | "E_TRANSFER"
  | "WECHAT"
  | null;

interface PaymentMetadata {
  netDays?: number;
  type?: string;
  paymentTermsId?: string;
}

interface PaymentData {
  placerCustomerId?: string;
  totalAmountCents?: number;
  shipments?: {
    selectedPricing?: { priceCents?: number };
    packages?: unknown[];
  }[];
}

interface PaymentResult extends PaymentData {
  paymentMethod: PaymentMethodType;
  paymentMetadata: PaymentMetadata;
}

interface PaymentSelectionStepProps {
  data: PaymentData;
  onNext: (data: PaymentResult) => void;
}

export function PaymentSelectionStep({
  data,
  onNext,
}: PaymentSelectionStepProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>(null);
  const [selectedPaymentTermsId, setSelectedPaymentTermsId] = useState<
    string | null
  >(null);

  // Get customer credit balance if customer is selected
  const { data: creditBalance } = useGetCustomerCreditBalance(
    data.placerCustomerId || ""
  );

  // Get customer's available payment terms (always fetch if customer is selected)
  const { data: customerPaymentTerms } = useGetCustomerPaymentTerms(
    data.placerCustomerId || ""
  );

  // Fetch all payment instructions
  const { data: paymentInstructions } = usePaymentInstructions();

  // Fetch customer data
  const customerIdentity = useCustomerIdentity(data.placerCustomerId);

  const getCustomerInfo = () => {
    if (!data.placerCustomerId) return null;
    const customer = customerIdentity.data;
    if (!customer) return null;

    const name =
      customer.firstName && customer.lastName
        ? `${customer.firstName} ${customer.lastName}`
        : customer.email || "Unknown Customer";

    return {
      name,
      email: customer.email,
    };
  };

  const getTotalCost = () => {
    if (!data.shipments) return 0;
    // pricing-selection writes selectedPricing.priceCents (now tax-inclusive,
    // matching what Stripe will run). The dialog footer reads the same field,
    // so we stay aligned.
    return data.shipments.reduce((sum: number, shipment) => {
      if (shipment.selectedPricing?.priceCents) {
        return sum + shipment.selectedPricing.priceCents;
      }
      return sum;
    }, 0);
  };

  // Helper function to get payment instructions for a specific method
  const getPaymentInstruction = (method: PaymentMethodType) => {
    if (!paymentInstructions || !method) return null;

    // If paymentInstructions is an array, find the matching method
    if (Array.isArray(paymentInstructions)) {
      return paymentInstructions.find(
        (instruction) => instruction.method === method && instruction.isActive
      );
    }

    // If it's a single instruction object, return it if method matches
    if (paymentInstructions && typeof paymentInstructions === "object") {
      return (paymentInstructions as { method: PaymentMethodType }).method ===
        method
        ? paymentInstructions
        : null;
    }

    return null;
  };

  const totalCost = getTotalCost();
  const hasCustomer = !!data.placerCustomerId;
  const hasSufficientCredit =
    creditBalance && creditBalance.balanceCents >= totalCost;

  const handleContinue = () => {
    const paymentMetadata: PaymentMetadata = {};

    // If NET terms selected, include the payment terms ID
    if (paymentMethod === "INVOICE" && selectedPaymentTermsId) {
      const selectedTerm = customerPaymentTerms?.paymentTerms.find(
        (t) => t.id === selectedPaymentTermsId
      );
      if (selectedTerm) {
        paymentMetadata.paymentTermsId = selectedPaymentTermsId;
      }
    }

    // Get payment instructions for the selected method
    const paymentInstruction = paymentMethod
      ? getPaymentInstruction(paymentMethod)
      : null;

    onNext({
      paymentMethodType: paymentMethod,
      ...(selectedPaymentTermsId && { paymentTermsId: selectedPaymentTermsId }),
      // Note: Credit is applied automatically when paymentMethodType === "CREDIT"
      paymentMetadata:
        Object.keys(paymentMetadata).length > 0 ? paymentMetadata : undefined,
      ...(paymentInstruction && {
        paymentInstructions: paymentInstruction,
      }),
    } as unknown as Parameters<typeof onNext>[0]);
  };

  const paymentMethods = [
    {
      value: null as PaymentMethodType,
      label: "Decide Later",
      description: "Create order without selecting payment method",
      icon: HelpCircle,
      available: true,
      category: "none",
    },
    {
      value: "SAVED_CARD" as PaymentMethodType,
      label: "Payment Link",
      description: "Generate Stripe payment link for customer to pay online",
      icon: Send,
      available: hasCustomer,
      requiredNote: !hasCustomer ? "Customer selection required" : null,
      category: "instant",
    },
    {
      value: "CREDIT" as PaymentMethodType,
      label: "Account Credit",
      description: "Use available account balance",
      icon: Wallet,
      // Credit is full-coverage-only and blocked while the wallet is frozen —
      // gate here so the server's 402/409 never has to bounce the order.
      available:
        hasCustomer && !creditBalance?.frozen && Boolean(hasSufficientCredit),
      requiredNote: !hasCustomer
        ? "Customer selection required"
        : creditBalance?.frozen
          ? "Credit wallet frozen — a deposit charge is under dispute"
          : (creditBalance?.balanceCents ?? 0) <= 0
            ? "No account credit available"
            : !hasSufficientCredit
              ? `Credit must cover the full order total (${formatCurrency(totalCost)})`
              : null,
      balance: creditBalance?.balanceCents,
      category: "balance",
    },
    {
      value: "INVOICE" as PaymentMethodType,
      label: "NET Terms",
      description: "Invoice with payment terms (NET 30/60/90)",
      icon: Calendar,
      available:
        hasCustomer &&
        customerPaymentTerms?.paymentTerms &&
        customerPaymentTerms.paymentTerms.length > 0,
      requiredNote: !hasCustomer
        ? "Customer selection required"
        : !customerPaymentTerms?.paymentTerms ||
            customerPaymentTerms.paymentTerms.length === 0
          ? "No NET terms available for this customer"
          : null,
      category: "deferred",
    },
    {
      value: "CASH" as PaymentMethodType,
      label: "Cash",
      description: "Customer pays cash on delivery, invoice generated",
      icon: Banknote,
      available: hasCustomer,
      requiredNote: !hasCustomer ? "Customer selection required" : null,
      category: "instant",
    },
    {
      value: "E_TRANSFER" as PaymentMethodType,
      label: "E-Transfer",
      description: "Customer pays first via e-transfer, invoice generated",
      icon: Send,
      available: hasCustomer,
      requiredNote: !hasCustomer ? "Customer selection required" : null,
      category: "instant",
    },
    {
      value: "WECHAT" as PaymentMethodType,
      label: "WeChat Pay",
      description: "Customer pays first via WeChat, invoice generated",
      icon: MessageSquare,
      available: hasCustomer,
      requiredNote: !hasCustomer ? "Customer selection required" : null,
      category: "instant",
    },
  ];

  // Group payment methods by category
  const instantMethods = paymentMethods.filter((m) => m.category === "instant");
  const deferredMethods = paymentMethods.filter(
    (m) => m.category === "deferred"
  );
  const balanceMethods = paymentMethods.filter((m) => m.category === "balance");
  const noSelectionMethods = paymentMethods.filter(
    (m) => m.category === "none"
  );

  return (
    <div className="space-y-6">
      {data.placerCustomerId && customerIdentity.error && (
        <div role="alert" className="text-sm text-destructive">
          Could not load selected customer.
          {customerIdentity.data && " Showing previously loaded identity."}
          <Button
            variant="outline"
            disabled={
              customerIdentity.isCoolingDown || customerIdentity.isFetching
            }
            onClick={() => void customerIdentity.refetch()}
          >
            Retry
          </Button>
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Select Payment Method
          </CardTitle>
          <CardDescription>
            Choose how you'd like to pay for this order
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Order Total */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Order Total</p>
                  <p className="text-2xl font-bold text-blue-800">
                    {formatCurrency(totalCost)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">
                    {data.shipments?.length ?? 0} shipment
                    {(data.shipments?.length ?? 0) !== 1 ? "s" : ""}
                  </p>
                  <p className="text-sm text-gray-600">
                    {data.shipments?.reduce(
                      (sum: number, s) => sum + (s.packages?.length ?? 0),
                      0
                    ) ?? 0}{" "}
                    package(s)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Info */}
          {hasCustomer &&
            (() => {
              const customerInfo = getCustomerInfo();
              return (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Customer:{" "}
                    <strong>
                      {customerInfo?.name ||
                        (customerIdentity.error
                          ? "Customer identity unavailable"
                          : "Loading...")}
                    </strong>
                    {customerInfo?.email && (
                      <span className="ml-2 text-muted-foreground">
                        ({customerInfo.email})
                      </span>
                    )}
                    {creditBalance && (
                      <span className="ml-2">
                        (Credit Balance:{" "}
                        {formatCurrency(creditBalance.balanceCents)})
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              );
            })()}

          {/* Payment Methods */}
          <RadioGroup
            value={paymentMethod || "null"}
            onValueChange={(value) =>
              setPaymentMethod(
                value === "null" ? null : (value as PaymentMethodType)
              )
            }
          >
            <div className="space-y-6">
              {/* No Selection Option */}
              <div className="space-y-2">
                {noSelectionMethods.map((method) => {
                  const Icon = method.icon;
                  const isDisabled = !method.available;

                  return (
                    <Card
                      key={method.value || "null"}
                      className={`relative ${isDisabled ? "opacity-60" : ""} ${
                        paymentMethod === method.value
                          ? "border-gray-500 bg-gray-50/50"
                          : ""
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <RadioGroupItem
                            value={method.value || "null"}
                            id={method.value || "null"}
                            disabled={isDisabled}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <Label
                              htmlFor={method.value || "null"}
                              className={`flex items-center gap-2 font-medium cursor-pointer ${
                                isDisabled ? "cursor-not-allowed" : ""
                              }`}
                            >
                              <Icon className="h-4 w-4" />
                              {method.label}
                              {paymentMethod === method.value && (
                                <Check className="h-4 w-4 text-gray-600 ml-auto" />
                              )}
                            </Label>
                            <p className="text-sm text-gray-600 mt-1">
                              {method.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Instant Payment */}
              {instantMethods.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Instant Payment
                  </h3>
                  {instantMethods.map((method) => {
                    const Icon = method.icon;
                    const isDisabled = !method.available;

                    return (
                      <Card
                        key={method.value}
                        className={`relative ${isDisabled ? "opacity-60" : ""} ${
                          paymentMethod === method.value
                            ? "border-blue-500 bg-blue-50/50"
                            : ""
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <RadioGroupItem
                              value={method.value ?? ""}
                              id={method.value ?? undefined}
                              disabled={isDisabled}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <Label
                                htmlFor={method.value ?? undefined}
                                className={`flex items-center gap-2 font-medium cursor-pointer ${
                                  isDisabled ? "cursor-not-allowed" : ""
                                }`}
                              >
                                <Icon className="h-4 w-4" />
                                {method.label}
                                {paymentMethod === method.value && (
                                  <Check className="h-4 w-4 text-blue-600 ml-auto" />
                                )}
                              </Label>
                              <p className="text-sm text-gray-600 mt-1">
                                {method.description}
                              </p>
                              {method.requiredNote && (
                                <p className="text-sm text-amber-600 mt-2">
                                  {method.requiredNote}
                                </p>
                              )}

                              {/* Display payment instructions from database if selected */}
                              {paymentMethod === method.value &&
                                method.value &&
                                (() => {
                                  const instruction = getPaymentInstruction(
                                    method.value
                                  );
                                  if (!instruction?.instructions) return null;

                                  return (
                                    <Alert className="mt-3 border-blue-200 bg-blue-50/50">
                                      <Info className="h-4 w-4 text-blue-600" />
                                      <AlertDescription className="text-sm whitespace-pre-wrap">
                                        <strong className="block mb-2">
                                          Payment Instructions:
                                        </strong>
                                        {instruction.instructions}
                                        {instruction.eTransferEmail && (
                                          <p className="mt-2 font-medium">
                                            E-Transfer Email:{" "}
                                            {instruction.eTransferEmail}
                                          </p>
                                        )}
                                        {instruction.wechatQRCode && (
                                          <p className="mt-2 text-xs text-muted-foreground">
                                            WeChat QR code will be shown in the
                                            invoice
                                          </p>
                                        )}
                                      </AlertDescription>
                                    </Alert>
                                  );
                                })()}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Deferred Payment (Invoice) */}
              {deferredMethods.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Deferred Payment (Invoice)
                  </h3>
                  {deferredMethods.map((method) => {
                    const Icon = method.icon;
                    const isDisabled = !method.available;

                    return (
                      <Card
                        key={method.value}
                        className={`relative ${isDisabled ? "opacity-60" : ""} ${
                          paymentMethod === method.value
                            ? "border-blue-500 bg-blue-50/50"
                            : ""
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <RadioGroupItem
                              value={method.value ?? ""}
                              id={method.value ?? undefined}
                              disabled={isDisabled}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <Label
                                htmlFor={method.value ?? undefined}
                                className={`flex items-center gap-2 font-medium cursor-pointer ${
                                  isDisabled ? "cursor-not-allowed" : ""
                                }`}
                              >
                                <Icon className="h-4 w-4" />
                                {method.label}
                                {paymentMethod === method.value && (
                                  <Check className="h-4 w-4 text-blue-600 ml-auto" />
                                )}
                              </Label>
                              <p className="text-sm text-gray-600 mt-1">
                                {method.description}
                              </p>
                              {method.requiredNote && (
                                <p className="text-sm text-amber-600 mt-2">
                                  {method.requiredNote}
                                </p>
                              )}

                              {/* NET Terms Selection */}
                              {method.value === "INVOICE" &&
                                paymentMethod === "INVOICE" &&
                                method.available &&
                                customerPaymentTerms && (
                                  <div className="mt-4 pl-6 border-l-2 border-gray-200">
                                    <div className="space-y-2">
                                      <Label>Select Payment Terms</Label>
                                      <Select
                                        value={selectedPaymentTermsId || ""}
                                        onValueChange={
                                          setSelectedPaymentTermsId
                                        }
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Choose payment terms" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {customerPaymentTerms.paymentTerms.map(
                                            (term) => (
                                              <SelectItem
                                                key={term.id}
                                                value={term.id}
                                              >
                                                <div className="flex flex-col">
                                                  <span>{term.name}</span>
                                                  {term.creditLimit && (
                                                    <span className="text-xs text-muted-foreground">
                                                      Credit Limit:{" "}
                                                      {formatCurrency(
                                                        term.creditLimit
                                                      )}
                                                    </span>
                                                  )}
                                                </div>
                                              </SelectItem>
                                            )
                                          )}
                                        </SelectContent>
                                      </Select>

                                      {selectedPaymentTermsId &&
                                        (() => {
                                          const selectedTerm =
                                            customerPaymentTerms.paymentTerms.find(
                                              (t) =>
                                                t.id === selectedPaymentTermsId
                                            );
                                          if (!selectedTerm) return null;

                                          return (
                                            <Alert className="mt-2">
                                              <Info className="h-4 w-4" />
                                              <AlertDescription className="space-y-1">
                                                <p className="font-medium">
                                                  NET Terms Details:
                                                </p>
                                                <p className="text-sm">
                                                  Payment Due:{" "}
                                                  {selectedTerm.paymentDueDays}{" "}
                                                  days
                                                </p>
                                                {selectedTerm.creditLimit && (
                                                  <>
                                                    <p className="text-sm">
                                                      Credit Limit:{" "}
                                                      {formatCurrency(
                                                        selectedTerm.creditLimit
                                                      )}
                                                    </p>
                                                    <p className="text-sm">
                                                      Available Credit:{" "}
                                                      {formatCurrency(
                                                        customerPaymentTerms.availableCredit ||
                                                          0
                                                      )}
                                                    </p>
                                                    <p className="text-sm">
                                                      Outstanding:{" "}
                                                      {formatCurrency(
                                                        customerPaymentTerms.outstandingBalance ||
                                                          0
                                                      )}
                                                    </p>
                                                  </>
                                                )}
                                                <p className="text-xs text-muted-foreground mt-2">
                                                  Invoiced on a{" "}
                                                  {selectedTerm.dayInterval}-day
                                                  billing cycle
                                                </p>
                                              </AlertDescription>
                                            </Alert>
                                          );
                                        })()}
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Account Balance */}
              {balanceMethods.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Account Balance
                  </h3>
                  {balanceMethods.map((method) => {
                    const Icon = method.icon;
                    const isDisabled = !method.available;

                    return (
                      <Card
                        key={method.value}
                        className={`relative ${isDisabled ? "opacity-60" : ""} ${
                          paymentMethod === method.value
                            ? "border-blue-500 bg-blue-50/50"
                            : ""
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <RadioGroupItem
                              value={method.value ?? ""}
                              id={method.value ?? undefined}
                              disabled={isDisabled}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <Label
                                htmlFor={method.value ?? undefined}
                                className={`flex items-center gap-2 font-medium cursor-pointer ${
                                  isDisabled ? "cursor-not-allowed" : ""
                                }`}
                              >
                                <Icon className="h-4 w-4" />
                                {method.label}
                                {paymentMethod === method.value && (
                                  <Check className="h-4 w-4 text-blue-600 ml-auto" />
                                )}
                              </Label>
                              <p className="text-sm text-gray-600 mt-1">
                                {method.description}
                              </p>

                              {/* Balance Display */}
                              {method.balance !== undefined && (
                                <div className="mt-2">
                                  <Badge
                                    variant={
                                      hasSufficientCredit
                                        ? "default"
                                        : "secondary"
                                    }
                                  >
                                    Available: {formatCurrency(method.balance)}
                                  </Badge>
                                </div>
                              )}

                              {method.requiredNote && (
                                <p className="text-sm text-amber-600 mt-2">
                                  {method.requiredNote}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </RadioGroup>

          {/* Information Note */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {paymentMethod === null ? (
                <>
                  Order will be created without a payment method. You can select
                  and process payment later from the order details page.
                </>
              ) : paymentMethod === "SAVED_CARD" ? (
                <>
                  A payment link will be generated for the customer to pay
                  online via Stripe.
                </>
              ) : paymentMethod === "CREDIT" ? (
                <>Payment will be processed immediately using account credit.</>
              ) : paymentMethod === "E_TRANSFER" ||
                paymentMethod === "WECHAT" ? (
                <>
                  Customer will receive payment instructions. Order will be
                  marked as paid once payment is received.
                </>
              ) : paymentMethod === "INVOICE" && selectedPaymentTermsId ? (
                <>
                  Order will be added to NET terms. Invoice will be generated
                  based on your payment terms.
                </>
              ) : paymentMethod === "CASH" ? (
                <>
                  Order will be marked for cash payment. Invoice will be
                  generated with payment instructions.
                </>
              ) : (
                <>Please select payment terms to continue with NET invoice.</>
              )}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Hidden button for sticky footer to trigger */}
      <button
        type="button"
        data-payment-continue
        onClick={handleContinue}
        style={{ display: "none" }}
        disabled={paymentMethod === "INVOICE" && !selectedPaymentTermsId}
      />
    </div>
  );
}
